import { Box, Card, Group, Stack } from '@mantine/core';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  TooltipItem,
  ChartEvent,
  ActiveElement,
} from 'chart.js';
import { GetEvChargingSessionStatsResponse } from 'api/actions/get-ev-charging-session-stats/get-ev-charging-session-stats-response';
import useLang from 'lang';
import theme from 'theme';
import P2Medium from 'theme/components/Typography/P2Medium';
import P4Regular from 'theme/components/Typography/P4Regular';
import { useMemo, useState } from 'react';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

interface MonthlyUsageChartCardProps {
  monthlyBreakdown: GetEvChargingSessionStatsResponse['monthlyBreakdown'];
}

/**
 * "Monthly overview" card: a bar chart of energy consumed (kWh) per month,
 * over the last 7 months.
 */
export default function MonthlyUsageChartCard({ monthlyBreakdown }: MonthlyUsageChartCardProps) {
  const { _t, dateFnsLocale } = useLang();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const labels = useMemo(
    () =>
      monthlyBreakdown.map(({ month }) => {
        const [year, monthNumber] = month.split('-').map(Number);
        const date = new Date(year, monthNumber - 1, 1);
        const label = new Intl.DateTimeFormat(dateFnsLocale.code, { month: 'short' }).format(date);
        return label.charAt(0).toUpperCase() + label.slice(1).replace('.', '');
      }),
    [monthlyBreakdown, dateFnsLocale]
  );

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          borderRadius: 5,
          borderSkipped: false as const,
          barPercentage: 0.6,
          label: _t('Consumption (kWh)'),
          data: monthlyBreakdown.map(({ consumptionKwh }) => consumptionKwh),
          backgroundColor: monthlyBreakdown.map((_, index) =>
            index === activeIndex ? theme.colors?.primary?.[3] : theme.colors?.primary?.[0]
          ),
          hoverBackgroundColor: theme.colors?.primary?.[3],
        },
      ],
    }),
    [_t, labels, monthlyBreakdown, activeIndex]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      onClick: (_event: ChartEvent, elements: ActiveElement[]) => {
        if (elements.length === 0) {
          return;
        }

        const index = elements[0].index;
        setActiveIndex((current) => (current === index ? null : index));
      },
      scales: {
        x: {
          ticks: { color: theme.colors?.gray?.[6], font: { size: 10 } },
          grid: { display: false },
        },
        y: { display: false },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (ctx: TooltipItem<'bar'>): string => `${ctx.parsed.y.toLocaleString()} kWh`,
          },
        },
      },
    }),
    []
  );

  return (
    <Stack gap={8}>
      <Group>
        <P4Regular color="gray.7">{_t('Monthly overview')}</P4Regular>
      </Group>
      <Card p={16} radius={10}>
        <Stack gap={12}>
          <P2Medium>{_t('kWh / month')}</P2Medium>
          <Box style={{ height: 120, width: '100%', position: 'relative' }}>
            <Bar data={chartData} options={chartOptions} />
          </Box>
        </Stack>
      </Card>
    </Stack>
  );
}
